import type { ChatSession, HomeChatGuidanceCard, WorkflowRecommendationType, WorkflowRecommendationEntryMode } from './types';
import { normalizeHomeChatCards } from './types';

type ConstrainedNativeWorkflowEntry = {
  workflowType: Extract<WorkflowRecommendationType, 'sow_verification' | 'one_time_construction'>;
  workflowHandoffReceipt: string;
  workflowEntryMode?: WorkflowRecommendationEntryMode;
};

/**
 * The constrained chooser is a separate server-issued boundary from
 * generic action cards.  Accept only its exact opaque native-entry receipt:
 * no browser-supplied flow id, stage, origin, or arbitrary workflow type can
 * become navigation authority here.
 */
export function extractConstrainedNativeWorkflowEntry(value: unknown): ConstrainedNativeWorkflowEntry | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const workflowType = record.workflow_type;
  if (
    record.status !== 'workflow_entry'
    || (workflowType !== 'sow_verification' && workflowType !== 'one_time_construction')
    || typeof record.selected_label !== 'string' || !record.selected_label.trim()
    || typeof record.workflow_handoff_receipt !== 'string' || !record.workflow_handoff_receipt.trim()
  ) return null;
  if (record.entry_mode !== undefined && (
    record.entry_mode !== 'standalone_price_estimator' || workflowType !== 'one_time_construction'
  )) return null;
  if (record.selected_label === 'Construction Price Estimator'
    && record.entry_mode !== 'standalone_price_estimator') return null;
  return {
    workflowType,
    workflowHandoffReceipt: record.workflow_handoff_receipt,
    ...(record.entry_mode === 'standalone_price_estimator'
      ? { workflowEntryMode: record.entry_mode } : {}),
  };
}

/** A persisted constrained choice must keep the same opaque public shape as a route response. */
export function constrainedWorkflowChoiceFromThread(thread: {
  constrained_session?: { kind?: unknown; chat_card?: unknown; entry_message?: unknown } | null;
}): { card: Record<string, unknown>; entryMessage: string | null } | null {
  const constrained = thread.constrained_session;
  if (constrained?.kind !== 'workflow_choice') return null;
  const card = constrained.chat_card;
  if (!card || typeof card !== 'object' || Array.isArray(card)) return null;
  const value = card as Record<string, unknown>;
  if (
    typeof value.card_id !== 'string' || !value.card_id.trim()
    || typeof value.prompt !== 'string' || !value.prompt.trim()
    || !Array.isArray(value.options) || value.options.length === 0
    || !value.options.every((option) => (
      option && typeof option === 'object' && !Array.isArray(option)
      && typeof (option as Record<string, unknown>).label === 'string'
      && Boolean((option as Record<string, unknown>).label)
      && typeof (option as Record<string, unknown>).selection_key === 'string'
      && Boolean((option as Record<string, unknown>).selection_key)
    ))
  ) return null;
  return {
    card: value,
    entryMessage: typeof constrained.entry_message === 'string' && constrained.entry_message.trim()
      ? constrained.entry_message : null,
  };
}

export function constrainedPurchaseGuidanceFromThread(thread: {
  constrained_session?: { kind?: unknown; chat_card?: unknown; entry_message?: unknown } | null;
  chat_cards?: unknown;
}): { card: HomeChatGuidanceCard; entryMessage: string | null } | null {
  const constrained = thread.constrained_session;
  if (constrained?.kind !== 'purchase_guidance' && constrained?.kind !== 'mission_partner_guidance') return null;
  const projected = normalizeHomeChatCards([constrained.chat_card]);
  const persisted = normalizeHomeChatCards(thread.chat_cards);
  const projectedCard = projected.find((item): item is HomeChatGuidanceCard => item.kind === constrained.kind);
  const matchingPersisted = persisted.find((item): item is HomeChatGuidanceCard => item.kind === constrained.kind
    && (!projectedCard || item.card_id === projectedCard.card_id));
  const card = projectedCard || matchingPersisted;
  if (!card) return null;
  return { card, entryMessage: typeof constrained.entry_message === 'string' && constrained.entry_message.trim()
    ? constrained.entry_message : null };
}

/** The constrained session list strips legacy gpc_binding; validate its replacement before using it for loading UI. */
export function hasConstrainedGpcBinding(session: ChatSession): boolean {
  const constrained = session.constrained_session;
  const binding = constrained?.binding;
  return constrained?.kind === 'gpc'
    && typeof binding?.gpc_chat_session_id === 'string'
    && Boolean(binding.gpc_chat_session_id.trim())
    && typeof binding.flow_id === 'string'
    && Boolean(binding.flow_id.trim());
}
